import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';

export default function TargetAudienceSection() {
  const { lang } = useLanguage();
  const t = translations.target[lang];

  return (
    <section id="target" data-testid="target-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Navy diagonal background */}
      <div className="absolute inset-0 bg-enbi-navy" style={{ clipPath: 'polygon(0 0, 100% 5%, 100% 100%, 0 95%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-[2px] bg-enbi-gold" />
              <span className="text-enbi-gold text-xs tracking-[0.3em] uppercase font-semibold">{t.label}</span>
            </div>
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-white uppercase tracking-tight mb-6">
              {t.headline}
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-10">
              {t.sub}
            </p>
            <div className="space-y-5">
              {t.items.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-start gap-4"
                  data-testid={`target-item-${i}`}
                >
                  <CheckCircle className="text-enbi-gold flex-shrink-0 mt-0.5" size={18} strokeWidth={1.5} />
                  <span className="text-white/80 text-sm leading-relaxed">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: Image with diagonal mask */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-6"
          >
            <div className="relative">
              <div
                className="overflow-hidden"
                style={{ clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0 90%)' }}
              >
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
                  alt="Business Team"
                  className="w-full h-[450px] object-cover"
                />
              </div>
              <div className="absolute -top-3 -right-3 w-full h-full border-2 border-enbi-gold/20 -z-10" style={{ clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0 90%)' }} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
